import { Checkbox, Divider, Radio, RadioGroup, Tag } from '@ads/components-react';
import { EyeIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type { QuestionAlternative } from '../../../hooks/useQuestionEdit';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Props do QuestionPreviewPanel */
export interface QuestionPreviewPanelProps {
  /** Enunciado da questão */
  readonly text: string;
  /** Alternativas da questão */
  readonly alternatives: QuestionAlternative[];
  /** IDs das alternativas corretas (uma para escolha única, várias para múltipla) */
  readonly correctIds: readonly string[];
  /**
   * ID numérico do tipo de componente da questão.
   * 1=SingleChoice, 2=MultipleChoice, 3=SingleLineText, 4=MultilineText,
   * 5=Numeric, 6=Grade, 7=Audio, 8=Attachment.
   * Quando ausente, o painel se comporta como tipo de escolha (compatibilidade).
   */
  readonly componentType?: number;
}

/** Mapeia o ID numérico do componentType para a chave i18n do fallback */
const FALLBACK_KEY_BY_TYPE: Record<number, string> = {
  1: 'edit.preview.fallback.singleChoice',
  2: 'edit.preview.fallback.multipleChoice',
  3: 'edit.preview.fallback.singleLineText',
  4: 'edit.preview.fallback.multilineText',
  5: 'edit.preview.fallback.numeric',
  6: 'edit.preview.fallback.grade',
  7: 'edit.preview.fallback.audio',
  8: 'edit.preview.fallback.attachment',
};

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Painel de preview read-only de uma questão, sempre visível abaixo do formulário
 * de edição. Reflete o estado atual (enunciado, alternativas, tipo) mostrando como
 * o aluno veria a questão. A alternativa correta é destacada e não é selecionável.
 *
 * @example
 * ```tsx
 * <QuestionPreviewPanel
 *   text={liveText}
 *   alternatives={liveAlternatives}
 *   correctIds={liveCorrectIds}
 *   componentType={componentTypeId}
 * />
 * ```
 */
export function QuestionPreviewPanel({
  text,
  alternatives,
  correctIds,
  componentType,
}: QuestionPreviewPanelProps) {
  const { t } = useTranslation('questionLibrary');

  const isChoiceType = componentType === 1 || componentType === 2;
  const isMultipleChoice = componentType === 2;
  const isSingleLineText = componentType === 3;
  const isMultilineText = componentType === 4;
  const showAlternatives = isChoiceType && alternatives.length > 0;
  const fallbackKey =
    componentType != null && FALLBACK_KEY_BY_TYPE[componentType]
      ? FALLBACK_KEY_BY_TYPE[componentType]
      : 'previewModal.openQuestion';

  return (
    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface">
      {/* Header */}
      <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-mini py-nano">
        <div className="flex items-center gap-nano">
          <EyeIcon className="size-xxxs text-ctx-content-title" />
          <span className="text-ctx-content-title ts-subtitle-xs">{t('edit.preview.title')}</span>
        </div>
        <Tag status="default" label={t('edit.preview.asStudent')} />
      </div>

      {/* Body */}
      <div className="flex flex-col gap-mini px-mini py-xs">
        {/* Enunciado */}
        <p
          dangerouslySetInnerHTML={{ __html: text }}
          className="rich-text-content text-ctx-content-base ts-paragraph-xs"
        ></p>

        {/* Alternativas — read-only, apenas a correta selecionada */}
        {showAlternatives && (
          <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border">
            <div className="border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-mini py-nano">
              <span className="text-ctx-content-title ts-subtitle-xxs">
                {t('edit.alternatives.columnHeader')}
              </span>
            </div>

            {(() => {
              const rows = alternatives.map((alt, idx) => (
                <div key={alt.id} className="flex w-full flex-col">
                  {/* biome-ignore lint/a11y/noLabelWithoutControl: label wraps ADS Checkbox/Radio that render native inputs internally */}
                  <label className="flex cursor-pointer items-center gap-micro rounded-sm px-xxs py-micro transition-all hover:bg-ctx-interactive-secondaryHover">
                    {isMultipleChoice ? (
                      <Checkbox size="md" checked={correctIds.includes(alt.id)} disabled />
                    ) : (
                      <Radio size="md" value={alt.id} />
                    )}

                    <span
                      className="[&_.code-display]:bg-gray-100 [&_.code-display]:px-2 [&_.code-display]:py-1 [&_.code-display]:font-mono [&_.code-display]:text-gray-800 text-ctx-content-base ts-paragraph-xs [&_*]:!font-base [&_*]:!text-xs [&_*]:!leading-lg [&_*]:!text-ctx-content-base [&_.code-display]:rounded-sm [&_.code-display]:text-sm" // Important is used to prevent the dangerouslySetInnerHTML from overriding some of the styles
                      dangerouslySetInnerHTML={{
                        __html: alt.value ?? '',
                      }}
                    />
                  </label>
                  {idx !== alternatives.length - 1 && <Divider />}
                </div>
              ));

              return isMultipleChoice ? (
                <div className="divide-y divide-ctx-layout-border">{rows}</div>
              ) : (
                <RadioGroup
                  value={correctIds[0] ?? ''}
                  className="divide-y divide-ctx-layout-border"
                >
                  {rows}
                </RadioGroup>
              );
            })()}
          </div>
        )}

        {/* Texto — campo de resposta do aluno */}
        {isSingleLineText && (
          <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body px-xs py-micro text-ctx-content-placeholder ts-paragraph-xs">
            {t('edit.preview.student.singleLineText')}
          </div>
        )}
        {isMultilineText && (
          <div className="min-h-[6rem] rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body px-xs py-micro text-ctx-content-placeholder ts-paragraph-xs">
            {t('edit.preview.student.multilineText')}
          </div>
        )}

        {/* Fallback por tipo de questão (escolha sem alternativas, numérica, nota, áudio, anexo) */}
        {!showAlternatives && !isSingleLineText && !isMultilineText && (
          <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body p-xs">
            <p className="italic text-ctx-content-placeholder ts-caption-xxs">
              {t(fallbackKey as never)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
